import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import userActions from "../../users/actions";
import dashboardActions from "../actions";
import imageActions from "../../images/actions";
import {
    getDefaultDashboardPath,
    getDashboardPathForView,
    getDashboardLocationForView,
    isAssetView,
    setDefaultDashboardView,
    resetBreadCrumpData,
    showAlert
} from "../../../config/utils";
import { setSessionDefaultClients } from "../../../utils/dashboardSession";
import { getDashboardBreadCrumbForView } from "../../common/components/DashboardSwitcher";

export const useDefaultProjectUpdate = ({ onUpdated } = {}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { getUserByIdResponse, getAllProjectsDropdownResponse, updateUserResponse } = useSelector(state => state.userReducer);
    const userId = localStorage.getItem("userId");

    const [projects, setProjects] = useState([]);
    const [isSubmitting, setSubmitting] = useState(false);
    const [isSwitching, setSwitching] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [assetManagementClient, setAssetManagementClient] = useState("");
    const [energyManagementClient, setEnergyManagementClient] = useState("");
    const shouldRedirectRef = useRef(false);
    const pendingProjectRef = useRef("");
    const ownUpdateRef = useRef(false);

    useEffect(() => {
        if (getUserByIdResponse?.success) {
            setAssetManagementClient(getUserByIdResponse?.assetmanagement_client_id?.id || "");
            setEnergyManagementClient(getUserByIdResponse?.energymanagement_client_id?.id || "");
        }
    }, [getUserByIdResponse]);

    useEffect(() => {
        if (getAllProjectsDropdownResponse?.projects) setProjects(getAllProjectsDropdownResponse.projects);
    }, [getAllProjectsDropdownResponse]);

    useEffect(() => {
        if (alertMessage) {
            showAlert(alertMessage);
            setAlertMessage("");
        }
    }, [alertMessage]);

    useEffect(() => {
        if (updateUserResponse) {
            // updateUser is shared — the FCA Projects menu calls it too; ignore other callers
            if (!ownUpdateRef.current) return;
            setSubmitting(false);
            const { success, error } = updateUserResponse;
            if (success) {
                shouldRedirectRef.current = true;
                dispatch(imageActions.resetImageEntityParams());
                dispatch(userActions.getUserById(userId));
            } else {
                setSwitching(false);
                ownUpdateRef.current = false;
                setAlertMessage(error || "Failed to update default project");
            }
        }
        return () => {
            // Picker and modal both mount this hook; a non-owner would clear the response first
            if (ownUpdateRef.current) dispatch(userActions.clearUpdateUserResponse());
        };
    }, [updateUserResponse]);

    useEffect(() => {
        if (!shouldRedirectRef.current) return;
        if (!getUserByIdResponse) return;
        const { assetmanagement_client_id, energymanagement_client_id } = getUserByIdResponse;
        if (assetmanagement_client_id) {
            setSessionDefaultClients(assetmanagement_client_id);
        } else if (energymanagement_client_id) {
            setSessionDefaultClients(energymanagement_client_id);
        }
        dispatch(dashboardActions.updateDefaultProject(pendingProjectRef.current));
        setAlertMessage("Default project updated successfully");
        shouldRedirectRef.current = false;

        setTimeout(() => {
            const defaultView = getUserByIdResponse?.default_dashboard_view;
            setDefaultDashboardView(defaultView);
            const newDashboardPath = getDashboardPathForView(defaultView);
            resetBreadCrumpData(
                isAssetView(defaultView) ? getDashboardBreadCrumbForView(defaultView) : { key: "main", name: "Dashboard", path: "/Dashboard", index: 0 }
            );
            if (location.pathname.toLowerCase() !== newDashboardPath) {
                history.push(getDashboardLocationForView(defaultView));
            }
            onUpdated?.();
            setSwitching(false);
            ownUpdateRef.current = false;
        }, 500);
    }, [getUserByIdResponse]);

    const resolveRedirectsToAssetDashboard = () => isAssetView(getUserByIdResponse?.default_dashboard_view);

    const isOnTargetDashboard = () => location.pathname.toLowerCase() === getDefaultDashboardPath();

    const applyProjectClients = projectId => {
        const selected = projects.find(project => project.id?.toString() === projectId?.toString());
        const clientId = selected ? selected.client_id : "";
        setAssetManagementClient(clientId);
        setEnergyManagementClient(clientId);
        return clientId;
    };

    const updateDefaultProject = (projectId, clientId) => {
        pendingProjectRef.current = projectId;
        ownUpdateRef.current = true;
        const resolvedClientId = clientId === undefined ? assetManagementClient : clientId;
        setSubmitting(true);
        setSwitching(true);
        dispatch(
            userActions.updateUser(
                {
                    default_project_id: projectId,
                    assetmanagement_client_id: resolvedClientId,
                    energymanagement_client_id: clientId === undefined ? energyManagementClient : clientId
                },
                userId
            )
        );
    };

    return {
        projects,
        isProjectsLoading: Boolean(getAllProjectsDropdownResponse?.isLoading),
        isSubmitting,
        isSwitching,
        applyProjectClients,
        updateDefaultProject,
        resolveRedirectsToAssetDashboard,
        isOnTargetDashboard
    };
};
